
import React, { useState } from "react";
import ProcurementList from "@/components/procurements/ProcurementList";
import OptimizedProcurementList from "@/components/procurements/OptimizedProcurementList";
import ProcurementSummaryCards from "@/components/procurements/ProcurementSummaryCards";
import SellerProcurementList from "@/components/procurements/SellerProcurementList";
import { Button } from "@/components/ui/button";
import { Plus, Loader2, Filter, Download, CalendarIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { resolveSourceNames, getSourceName, type ResolvedSourceMap } from "@/services/discrepancySourceResolver";

const STATUSES = [
  { value: "all", label: "All Statuses" },
  { value: "initiated", label: "Initiated" },
  { value: "pending_finance", label: "Pending Finance" },
  { value: "awaiting_approval", label: "Awaiting Approval" },
  { value: "finance_approved", label: "Finance Approved" },
  { value: "price_confirmed", label: "Price Confirmed" },
  { value: "delivered", label: "Delivered" },
  { value: "completed", label: "Completed" },
  { value: "rejected", label: "Rejected" },
];

const ProcurementOverview: React.FC = () => {
  const { userRole, user } = useAuth();
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState<Date | undefined>();
  const [exportEndDate, setExportEndDate] = useState<Date | undefined>();
  const [exportStatus, setExportStatus] = useState("all");

  const isFinanceOrAdmin = userRole === 'finance' || userRole === 'admin';
  const isSeller = userRole === 'seller';

  const handleNewPurchase = () => {
    setIsNavigating(true);
    navigate("/procurements/new");
    setTimeout(() => setIsNavigating(false), 100);
  };

  const fetchFilteredProcurements = async () => {
    let query = supabase
      .from('procurements')
      .select(`
        *,
        requestor:user_profiles!fk_procurements_requestor(first_name, last_name),
        items:procurement_items(*)
      `)
      .order('created_at', { ascending: false });

    if (isSeller && user?.id) {
      query = query.eq('requestor_id', user.id);
    }
    if (exportStatus && exportStatus !== 'all') {
      query = query.eq('status', exportStatus);
    }
    if (exportStartDate) {
      query = query.gte('created_at', exportStartDate.toISOString());
    }
    if (exportEndDate) {
      const endOfDay = new Date(exportEndDate);
      endOfDay.setHours(23, 59, 59, 999);
      query = query.lte('created_at', endOfDay.toISOString());
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  };

  const mapToExportRows = (procurements: any[], sourceMap: ResolvedSourceMap) => {
    return procurements.map(proc => {
      const sourceRef = {
        source_type: proc.source_type,
        source_id: proc.source_id || proc.farm_id || proc.farmer_id || proc.coordinator_id,
      };

      return {
        'ID': proc.id?.slice(0, 8),
        'Date': proc.created_at ? format(new Date(proc.created_at), 'yyyy-MM-dd') : '',
        'Source Type': proc.source_type || '',
        'Source Name': getSourceName(sourceMap, sourceRef) || 'N/A',
        'Product': proc.product_name || (proc.items?.length ? proc.items.map((i: any) => i.product_name).join(', ') : 'N/A'),
        'Quantity (kg)': proc.total_quantity || 0,
        'Total Amount (Br)': proc.total_amount || 0,
        'Status': proc.status?.replace(/_/g, ' ') || '',
        'Requestor': proc.requestor ? `${proc.requestor.first_name || ''} ${proc.requestor.last_name || ''}`.trim() : '',
        'Expected Delivery': proc.expected_delivery_date ? format(new Date(proc.expected_delivery_date), 'yyyy-MM-dd') : '',
      };
    });
  };

  const handleExport = async (type: 'csv' | 'excel') => {
    setIsExporting(true);
    try {
      const data = await fetchFilteredProcurements();
      if (!data.length) {
        toast.info('No procurements match the selected filters');
        return;
      }
      const sourceMap = await resolveSourceNames(
        data.map((p: any) => ({
          source_type: p.source_type,
          source_id: p.source_id || p.farm_id || p.farmer_id || p.coordinator_id,
        }))
      );
      const rows = mapToExportRows(data, sourceMap);
      const filename = `procurements_${format(new Date(), 'yyyy-MM-dd')}`;

      if (type === 'csv') {
        const headers = Object.keys(rows[0]);
        const csv = [
          headers.join(','),
          ...rows.map(r => headers.map(h => `"${(r as any)[h] ?? ''}"`).join(','))
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Procurements');
        XLSX.writeFile(wb, `${filename}.xlsx`);
      }
      toast.success(`Exported ${rows.length} procurements`);
      setExportDialogOpen(false);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export procurements');
    } finally {
      setIsExporting(false);
    }
  };

  const handleResetExportFilters = () => {
    setExportStartDate(undefined);
    setExportEndDate(undefined);
    setExportStatus("all");
  };

  return (
    <div className="h-full space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">
          {isFinanceOrAdmin ? "Procurement Management" : "My Procurements"}
        </h1>
        <div className="flex gap-2">
          <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Export Procurements</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={exportStatus} onValueChange={setExportStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !exportStartDate && "text-muted-foreground")}>
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {exportStartDate ? format(exportStartDate, 'PP') : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={exportStartDate} onSelect={setExportStartDate} initialFocus className={cn("p-3 pointer-events-auto")} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !exportEndDate && "text-muted-foreground")}>
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {exportEndDate ? format(exportEndDate, 'PP') : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={exportEndDate} onSelect={setExportEndDate} initialFocus className={cn("p-3 pointer-events-auto")} />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={handleResetExportFilters}>
                  Reset Filters
                </Button>
              </div>
              <DialogFooter className="flex gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => handleExport('csv')} disabled={isExporting}>
                  {isExporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Export CSV
                </Button>
                <Button onClick={() => handleExport('excel')} disabled={isExporting}>
                  {isExporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Export Excel
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {userRole === 'admin' && (
            <Button
              variant="outline"
              onClick={() => navigate("/procurements/filter")}
            >
              <Filter className="mr-2 h-4 w-4" />
              Filter Procurements
            </Button>
          )}
          <Button onClick={handleNewPurchase} disabled={isNavigating}>
            {isNavigating ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            New Purchase
          </Button>
        </div>
      </div>

      {isFinanceOrAdmin && <ProcurementSummaryCards />}

      {isFinanceOrAdmin ? (
        <OptimizedProcurementList />
      ) : (
        <SellerProcurementList />
      )}
    </div>
  );
};

export default ProcurementOverview;
